/**
 * Turning whatever the trainer picked into the square JPEG the backend accepts.
 *
 * The DOM half of the avatar upload — decode, crop, encode. The arithmetic is `model/avatarCrop`,
 * which is pure and tested; everything here needs a document and is therefore deliberately thin.
 *
 * ── Why this app re-encodes at all ──────────────────────────────────────────────────────────
 * `PUT /auth/me/avatar` accepts `image/jpeg` and `image/webp` only (`Media/Services/ImageSanitizer`),
 * caps the file at 2 MiB and the whole request at 3 MB (`AuthController`). A phone photograph is a
 * 4 MB JPEG or, on an iPhone handed to a browser, a PNG — so sending the picked file directly fails
 * for most trainers, and fails with a coded 400 they cannot act on. Re-encoding means the picker can
 * accept PNG too: what leaves here is always a JPEG inside the cap, whatever went in.
 *
 * The server strips metadata as defence in depth; drawing to a canvas has already dropped the EXIF
 * — including the GPS coordinates a phone photograph carries — before a byte leaves the browser.
 */

import { avatarCrop, exceedsAvatarDecodeCeiling } from '../model/avatarCrop';

/** What the file picker offers. PNG is accepted here and leaves as JPEG — see the header. */
export const AVATAR_ACCEPT = 'image/jpeg,image/webp,image/png';

/**
 * A ceiling on the file the trainer may PICK, which is not the backend's cap on what we send.
 *
 * It exists so a 60 MB raw photograph is refused with a sentence instead of locking the tab while
 * the browser decodes it. 10 MB comfortably covers any phone camera.
 */
export const AVATAR_MAX_PICKED_BYTES = 10 * 1024 * 1024;

/** Quality 0.8 on a 512px square lands around 40–60 KB, an order of magnitude inside the 2 MiB cap. */
const JPEG_QUALITY = 0.8;

export type AvatarPrepareFailure = 'badType' | 'tooLarge' | 'tooManyPixels' | 'failed';

export type AvatarPrepareResult =
  | { readonly ok: true; readonly blob: Blob }
  | { readonly ok: false; readonly reason: AvatarPrepareFailure };

function decode(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.addEventListener('load', () => {
      // Revoked as soon as the bitmap exists: an object URL held open keeps the whole file in
      // memory for the life of the document, and this one is 10 MB at the limit.
      URL.revokeObjectURL(url);
      resolve(image);
    });
    image.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('The picked file could not be decoded as an image.'));
    });
    image.src = url;
  });
}

/**
 * Decode, cut the largest centred square, scale it down if it is larger than 512, and encode a JPEG.
 *
 * Reports rather than throws: every outcome is a sentence the form renders, and a picked file that
 * turns out not to be an image is the trainer's ordinary mistake rather than an exception.
 */
export async function prepareAvatar(file: File): Promise<AvatarPrepareResult> {
  if (!file.type.startsWith('image/')) return { ok: false, reason: 'badType' };
  // SVG is `image/*` and the backend refuses it, but the reason to stop it here is that an `<img>`
  // decoding one runs the document's own scripts and CSS. The picker's `accept` is advisory —
  // a drag-and-drop never sees it.
  if (file.type === 'image/svg+xml') return { ok: false, reason: 'badType' };
  if (file.size > AVATAR_MAX_PICKED_BYTES) return { ok: false, reason: 'tooLarge' };

  let image: HTMLImageElement;
  try {
    image = await decode(file);
  } catch {
    // A `.jpg` that is not a JPEG lands here, which is a type problem rather than a size one.
    return { ok: false, reason: 'badType' };
  }

  if (exceedsAvatarDecodeCeiling(image.naturalWidth, image.naturalHeight)) {
    return { ok: false, reason: 'tooManyPixels' };
  }

  const crop = avatarCrop(image.naturalWidth, image.naturalHeight);
  if (crop.sourceEdge === 0) return { ok: false, reason: 'badType' };

  const canvas = document.createElement('canvas');
  canvas.width = crop.outputEdge;
  canvas.height = crop.outputEdge;

  const context = canvas.getContext('2d');
  if (context === null) return { ok: false, reason: 'failed' };
  context.drawImage(
    image,
    crop.sourceX,
    crop.sourceY,
    crop.sourceEdge,
    crop.sourceEdge,
    0,
    0,
    crop.outputEdge,
    crop.outputEdge,
  );

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
  });

  return blob === null ? { ok: false, reason: 'failed' } : { ok: true, blob };
}
